import { computed, watch } from 'vue';
import type { ColumnDef } from '@tanstack/vue-table';
import { locationFacetValue, locationLabel } from '@/composables/records/recordsFormat';
import { BLANK_FACET_VALUE, type RecordFacetDefinitions } from '@/composables/records/useRecordsAggregation';
import { useRecordsPage } from '@/composables/records/useRecordsPage';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys, useTransfers } from '@/plugins/store/actions/queries/transfers';
import { RECORD_ACTIONS_COLUMN_ID, type TransferRow } from '@/types';
import { columnPresence, TOGGLE_COLUMN_IDS, type TransferToggleColumnId } from './useTransfersColumnVisibility';

const HEADINGS_KEY = 'views.pigs.records.table.headings';

/** The bulk transfer offered above the table: one transfer from a location to a barn divided into its sections (the transfer form, in bulk). */
export const BULK_CREATE = {
    formId: 3,
    labelKey: 'views.pigs.transfers.table.createButton',
    locationStep: 'regular',
} as const;

/** i18n key for each toggleable column's heading, read off the same shared `records` namespace every
 * other records page's headings live under. */
const HEADING_KEYS: Record<TransferToggleColumnId, string> = {
    sow_genetics: `${HEADINGS_KEY}.sowGenetics`,
    boar_genetics: `${HEADINGS_KEY}.boarGenetics`,
    gender: `${HEADINGS_KEY}.gender`,
    created_by: `${HEADINGS_KEY}.createdBy`,
};

/**
 * What the Transfers page filters on: both ends of the move, plus the three presence-driven facts
 * about the pigs themselves.
 *
 * Every accessor falls back to the blank option rather than reading through the chain: a section is
 * absent whenever the registration was made straight on a barn, and the whole To chain is absent on
 * a move whose destination can no longer be named — a location deleted in a reorganisation, or one
 * in another directory. These run over every loaded row on every render, so a chain read without
 * the guard would take the page down over one such row rather than blank one cell.
 */
function facets(): RecordFacetDefinitions<TransferRow> {
    const blank = i18n.global.t('views.pigs.records.table.texts.blank');

    return {
        from_barn: {
            value: (row) => locationFacetValue(row.from_farm, row.from_barn),
            label: (row) => locationLabel(row.from_farm, row.from_barn) || blank,
        },
        from_section: {
            value: (row) => row.from_section?.id ?? BLANK_FACET_VALUE,
            label: (row) => row.from_section?.name ?? blank,
        },
        to_barn: {
            value: (row) => locationFacetValue(row.to_farm, row.to_barn),
            label: (row) => locationLabel(row.to_farm, row.to_barn) || blank,
        },
        to_section: {
            value: (row) => row.to_section?.id ?? BLANK_FACET_VALUE,
            label: (row) => row.to_section?.name ?? blank,
        },
        sow_genetics: {
            value: (row) => row.sow_genetics ?? BLANK_FACET_VALUE,
            label: (row) => row.sow_genetics ?? blank,
        },
        boar_genetics: {
            value: (row) => row.boar_genetics ?? BLANK_FACET_VALUE,
            label: (row) => row.boar_genetics ?? blank,
        },
        gender: {
            value: (row) => row.gender ?? BLANK_FACET_VALUE,
            label: (row) => row.gender ?? blank,
        },
    };
}

/**
 * The columns, in the order a move reads: when it happened, where the pigs left, where they arrived,
 * how many of them at what weight, then whichever of the presence-driven facts about the pigs
 * themselves the reader currently has on.
 *
 * Every filterable column here carries no accessor: it is unsorted, its cells render from the whole
 * row, and the values its header facet compares on belong to the filtering above. Give one an
 * accessor the day a column needs in-table sorting.
 */
function columns(isVisible: (columnId: TransferToggleColumnId) => boolean): ColumnDef<TransferRow>[] {
    const base: ColumnDef<TransferRow>[] = [
        { id: 'date', accessorKey: 'date', header: i18n.global.t('views.pigs.records.table.headings.date'), enableSorting: true },
        { id: 'from_barn', header: i18n.global.t('views.pigs.records.table.headings.fromLocation'), meta: { filterable: true } },
        { id: 'from_section', header: i18n.global.t('views.pigs.records.table.headings.fromSection'), meta: { filterable: true } },
        { id: 'to_barn', header: i18n.global.t('views.pigs.records.table.headings.toLocation'), meta: { filterable: true } },
        { id: 'to_section', header: i18n.global.t('views.pigs.records.table.headings.toSection'), meta: { filterable: true } },
        { id: 'number', accessorKey: 'number', header: i18n.global.t('views.pigs.records.table.headings.number'), enableSorting: true, meta: { align: 'right' } },
        { id: 'total_weight', accessorKey: 'total_weight', header: i18n.global.t('views.pigs.records.table.headings.totalWeight'), enableSorting: true, meta: { align: 'right' } },
        { id: 'avg_weight', accessorKey: 'avg_weight', header: i18n.global.t('views.pigs.records.table.headings.avgWeight'), enableSorting: true, meta: { align: 'right' } },
    ];

    for (const columnId of TOGGLE_COLUMN_IDS) {
        if (columnId !== 'created_by' && isVisible(columnId)) {
            base.push({ id: columnId, header: i18n.global.t(HEADING_KEYS[columnId]), meta: { filterable: true } });
        }
    }

    // Sortable rather than filterable, and off until the reader turns it on.
    if (isVisible('created_by')) {
        base.push({ id: 'created_by', accessorKey: 'created_by', header: i18n.global.t(HEADING_KEYS.created_by), enableSorting: true });
    }

    return [...base, { id: RECORD_ACTIONS_COLUMN_ID, header: '', meta: { align: 'right' } }];
}

/**
 * The Transfers page's configuration of the shared records page, plus the column-visibility control
 * over its three presence-driven facts about the pigs themselves, plus Created by. None of them are hard-gated - see
 * `useTransfersColumnVisibility` - so availability never varies; only the reader's own stored choice,
 * else a presence-aware default (on once a loaded row actually carries it; Created by always defaults off), decides what's shown.
 */
export function useTransfersPage() {
    const page = useRecordsPage<TransferRow>({
        records: { use: useTransfers, rootKey: queryKeys.getTransfersRoot },
        facets,
    });

    const presence = computed(() => columnPresence(page.rows.value));

    /**
     * Drops a facet's own active filter selection once its column is no longer on the table - a
     * hidden column has no chip left to clear it from, and `applyFacetFilters` would otherwise keep
     * matching rows against a filter the reader can no longer see or reach.
     */
    function clearFilterIfHidden(id: TransferToggleColumnId) {
        if (id in page.columnFilters.value) {
            const next = { ...page.columnFilters.value };

            delete next[id];
            page.columnFilters.value = next;
        }
    }

    const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<TransferToggleColumnId>({
        storageKey: 'pigs.transfers.columnVisibility',
        columnIds: TOGGLE_COLUMN_IDS,
        isAvailable: () => true,
        defaultVisible: (id) => id !== 'created_by' && presence.value[id],
        label: (id) => i18n.global.t(HEADING_KEYS[id]),
        onHide: clearFilterIfHidden,
    });

    // A period change (or any other change to the loaded set) can drop a column's only data, hiding
    // it via its own presence-based default even without a stored override - not just a user's own
    // toggle. Prune its filter the same way.
    watch(presence, () => {
        for (const id of TOGGLE_COLUMN_IDS) {
            if (!isVisible(id)) {
                clearFilterIfHidden(id);
            }
        }
    });

    // Computed rather than a constant so the headings follow a language switch.
    return { ...page, columns: computed(() => columns(isVisible)), columnToggleOptions, toggleColumn, setAllColumns };
}
